import { TextField, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import { Box, Button, Icon } from "../common/components";
import DialogTitleWithCloseButton from "../common/components/Dialog/DialogTitleWithCloseButton/DialogTitleWithCloseButton";
import DesignService from "../service/DesignService";
import { palette, spacing } from "../styles/theme";
import { ROUTES } from "../constant/common.constant";
import { useThemeBreakpoints } from "../utils/themeUtils";
import { useTeam } from "../context/Team";

type FlattenTeamProject = {
  id: string;
  key: string;
  type: string;
  name: string;
  user_id: null;
  team_id: string;
};

interface Props {
  open: boolean;
  onClose: () => void;
  designId: string;
}

const AddToProjectModal = ({ open, onClose, designId }: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useThemeBreakpoints();
  const [selectedProject, setSelectedProject] =
    useState<FlattenTeamProject | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setSelectedProject(null);
    onClose();
  };

  const { teams } = useTeam();

  const navigate = useNavigate();
  const onAddToProject = useCallback(
    async (newProject: FlattenTeamProject) => {
      setIsLoading(true);
      const addDesignToProjectObj = {
        designId: designId,
        newProjectId: newProject?.id,
      };
      await DesignService.addDesignToProject(addDesignToProjectObj);

      navigate(
        generatePath(ROUTES.PROJECT, {
          projectId: newProject.id,
          teamId: newProject.team_id,
        })
      );
      setIsLoading(false);
    },
    [designId, navigate]
  );

  const teamProjects = useMemo(() => {
    const unFlattenedArray = teams.map((team) => {
      return {
        key: team.id,
        id: team.id,
        name: team.name,
        type: "team",
        projects: team.projects,
        url: (projectId: string) =>
          generatePath(ROUTES.PROJECT, { projectId, teamId: team.id }),
      };
    });

    const flatObject: FlattenTeamProject[] = [];
    unFlattenedArray.forEach((team) => {
      team.projects.forEach((project) => {
        const individualProjectObj = {
          id: project.id,
          key: project.id,
          type: team.name,
          name: project.name,
          user_id: null,
          team_id: team.id,
        } as FlattenTeamProject;
        return flatObject.push(individualProjectObj);
      });
    });
    return flatObject;
  }, [teams]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth={true}
      maxWidth={"md"}
      fullScreen={isMobile}
    >
      <DialogTitleWithCloseButton
        id="customized-dialog-title"
        onClose={handleClose}
      >
        {t("add_to_project_modal.title")}
      </DialogTitleWithCloseButton>
      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          gap={{ xs: spacing.regularMobile, lg: spacing.regularDesktop }}
        >
          <Box>
            <Autocomplete
              id="moveProjects"
              options={teamProjects}
              groupBy={(option) => option.type}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => <TextField {...params} />}
              clearIcon={false}
              popupIcon={
                <Icon size={24} color={palette.grey.primary} icon="search" />
              }
              filterOptions={(options, state) => {
                const value = state.inputValue.toUpperCase();
                return options.filter(
                  (o) =>
                    o.type.toUpperCase().includes(value) ||
                    o.name.toUpperCase().includes(value)
                );
              }}
              value={selectedProject}
              onChange={(event, newValue) => {
                setSelectedProject(newValue);
              }}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.name + option.id}>
                    {option.name}
                  </li>
                );
              }}
              sx={{
                "& .MuiAutocomplete-popupIndicator": { transform: "none" },
              }}
            />
          </Box>
          {isMobile && (
            <Button
              onClick={() => selectedProject && onAddToProject(selectedProject)}
              disabled={selectedProject === null}
              isLoading={isLoading}
              fullWidth
            >
              <Typography variant="actionBoldBigIcon">
                {t("add_to_project_modal.action")}
              </Typography>
            </Button>
          )}
        </Box>
      </DialogContent>
      {!isMobile && (
        <DialogActions>
          <Button
            onClick={() => selectedProject && onAddToProject(selectedProject)}
            disabled={selectedProject === null}
            isLoading={isLoading}
          >
            <Typography variant="actionBoldBigIcon">
              {t("add_to_project_modal.action")}
            </Typography>
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default memo(AddToProjectModal);
